import React, { Fragment } from "react";
import authApi from "../../../utils/api/authApi";
import { Stack, Button, Paper, TextField } from "@mui/material";
import * as Validation from "../utils/AuthZodValidation";
import { LOGIN } from "../../../enums/UrlPaths";
import { useForm } from "react-hook-form";
import {
  CHANGE_PASSWORD,
  CHANGE_PASSWORD_WITH_TOKEN,
} from "../../../enums/ApiPaths";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CenterScreen from "../../../components/wrappers/CenterScreen";
import {
  getBearerToken,
  setNeedToChangePassword,
} from "../../../utils/Functions";
import { SnackbarContext } from "../../../contexts/Snackbar";
import useUser, { useGetUserId } from "../../../hooks/useUser";
import AuthFooter from "../login/component/AuthFooter";
import SimpleAlert from "../../../components/display/SimpleAlert";
import { useQueryClient } from "@tanstack/react-query";
import { LOGGED_IN_USER } from "../../../enums/QueryKeys";
import HeaderNoAuth from "../../../components/display/HeaderNoAuth";
// This page is for employees/clinics to change their password
export default function ChangePasswordPage() {
  const { state } = useLocation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { handleSnackbar } = React.useContext(SnackbarContext);
  const userId = useGetUserId();
  const { booleans } = useUser();
  const { register, handleSubmit, formState } = useForm({
    defaultValues: Validation.defaultPasswordRestValues,
    resolver: zodResolver(Validation.usePasswordResetResolver()),
  });

  const { errors } = formState;

  const changePasswordSubmit = async (e) => {
    if (state != null && state?.userId != null) {
      changePasswordWithHeader(e, CHANGE_PASSWORD + "/" + state.userId);
    } else if (userId) {
      changePasswordWithHeader(e, CHANGE_PASSWORD);
    } else {
      changePasswordWithoutHeader(e);
    }
  };

  function invaldiateUser() {
    queryClient.invalidateQueries([LOGGED_IN_USER]);
    setNeedToChangePassword();
  }

  async function changePasswordWithoutHeader(e) {
    const urlSearchString = window.location.search;
    const params = new URLSearchParams(urlSearchString);
    const token = params.get("token");
    const data = {
      password: e.password,
    };
    try {
      let res = await authApi.post(
        CHANGE_PASSWORD_WITH_TOKEN + "?token=" + token,
        data
      );
      invaldiateUser();
      handleSnackbar("success", "success");
      navigate(LOGIN);
    } catch (error) {
      handleSnackbar(error?.response?.data, "error");
    }
  }

  async function changePasswordWithHeader(e, path) {
    const data = {
      password: e.password,
    };
    try {
      const header = await getBearerToken();
      let res = await authApi.post(path, data, {
        headers: header,
      });

      invaldiateUser();
      handleSnackbar("PasswordChanged", "success");
      navigate(-1);
    } catch (error) {
      handleSnackbar(error?.response?.data, "error");
    }
  }

  function handleNavigate() {
    const urlSearchString = window.location.search;
    const params = new URLSearchParams(urlSearchString);
    const token = params.get("token");
    if (token) {
      navigate(LOGIN);
    } else {
      navigate(-1);
    }
  }

  function handleButtonTitle() {
    return "Back";
  }

  return (
    <Fragment>
      <CenterScreen minHeight="78vh">
        <Paper sx={{ p: 2, width: { xs: "90%", md: "35%" } }}>
          <Stack sx={{ mb: 2 }}>
            {booleans.needToChangePassword ? (
              <SimpleAlert
                message={"PleaseUpdateYourPasswordForSecurityReasons"}
                severity={"warning"}
              />
            ) : null}
          </Stack>
          <form onSubmit={handleSubmit(changePasswordSubmit)}>
            <Stack spacing={2} justifyContent={"center"} alignItems={"center"}>
              <TextField
                sx={{ width: "100%", m: 2 }}
                inputProps={{ maxLength: 64 }}
                label={t("Password")}
                type="password"
                error={!!errors.password}
                helperText={errors.password?.message}
                {...register("password")}
              />
              <TextField
                sx={{ width: "100%", m: 2 }}
                inputProps={{ maxLength: 64 }}
                label={t("ConfirmPassword")}
                type="password"
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword?.message}
                {...register("confirmPassword")}
              />
              <Button type="submit" variant="contained" sx={{ width: "100%" }}>
                {t("ChangePassword")}
              </Button>
              {booleans.needToChangePassword ? null : (
                <Button
                  sx={{ width: "100%" }}
                  variant="outlined"
                  onClick={() => handleNavigate()}
                >
                  {t(handleButtonTitle())}
                </Button>
              )}
            </Stack>
          </form>
        </Paper>
      </CenterScreen>
      <AuthFooter />
    </Fragment>
  );
}
